import { useState, useCallback } from 'react';
import {
  Outlet,
  Link,
  useParams,
  useChildMatches,
  useNavigate,
  useSearch,
  Navigate,
} from '@tanstack/react-router';
import { useGetWorkflowDefinition } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { CircleNotchIcon, CopyIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import paths from 'src/paths';
import { DuplicateWorkflowModal } from './components/DuplicateWorkflowModal';
import {
  ForegroundAgentChat,
  type EmailRuleConfig,
  type SaveAndRunWorkflowPayload,
} from '@/components/common/ForegroundAgentChat';
import {
  WorkflowEditorProvider,
  useWorkflowEditorContext,
} from './context/WorkflowEditorContext';
import { rpcClient } from '@/api/rpc/client';

function WorkflowDefinitionLayoutPageInner() {
  const { workflowDefinitionId } = useParams({
    from: '/authenticated/workflows/$workflowDefinitionId',
  });
  const navigate = useNavigate();
  const { edit, aiCreationId, emailRuleOptionId } = useSearch({
    strict: false,
  }) as {
    edit?: boolean;
    aiCreationId?: string;
    emailRuleOptionId?: string;
  };
  const { addNode } = useWorkflowEditorContext();

  const isCreateMode = workflowDefinitionId === 'new';

  const {
    data: definition,
    isLoading,
    error,
  } = useGetWorkflowDefinition(isCreateMode ? undefined : workflowDefinitionId);

  const activeChildRoute = useChildMatches({
    select: (matches) => matches[0],
  });
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleCreateWorkflowWithEmailRule = useCallback(
    async (config: EmailRuleConfig) => {
      const filters: {
        from?: string[];
        subject?: string[];
      } = {};

      if (config.filters.subjectContains) {
        filters.subject = [config.filters.subjectContains];
      }
      if (config.filters.senderDomainContains) {
        filters.from = [config.filters.senderDomainContains];
      }
      if (config.filters.senderEmailContains) {
        filters.from = [
          ...(filters.from ?? []),
          config.filters.senderEmailContains,
        ];
      }

      const response = await rpcClient.communications.emailRules.create({
        ruleName: config.ruleName,
        filters,
      });

      await rpcClient.communications.emailRules.events.createAndLink({
        ruleId: response.rule.id,
        name: config.eventName,
      });
    },
    [],
  );

  const handleSaveAndRunWorkflowOnInvoices = useCallback(
    async (_payload: SaveAndRunWorkflowPayload) => {},
    [],
  );

  if (isCreateMode && !edit) {
    return (
      <Navigate
        to={paths.authenticated.workflows.definition}
        params={{ workflowDefinitionId: 'new' }}
        search={{
          edit: true,
          ...(aiCreationId && { aiCreationId }),
          ...(emailRuleOptionId && { emailRuleOptionId }),
        }}
        replace
      />
    );
  }

  if (!isCreateMode && isLoading) {
    return (
      <div className="p-2">
        <div className="flex items-center justify-center py-12">
          <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isCreateMode && (error || !definition)) {
    return (
      <div className="p-2">
        <div className="text-center py-12">
          <p className="text-destructive">
            {error instanceof Error
              ? error.message
              : 'Failed to load workflow definition'}
          </p>
        </div>
      </div>
    );
  }

  const isEditMode = !!edit;

  const activeRouteId = activeChildRoute?.routeId;
  let activeTab = 'overview';
  if (
    activeRouteId === '/authenticated/workflows/$workflowDefinitionId/steps'
  ) {
    activeTab = 'steps';
  } else if (
    activeRouteId === '/authenticated/workflows/$workflowDefinitionId/runs'
  ) {
    activeTab = 'runs';
  }

  if (isEditMode || isCreateMode) {
    return (
      <div className="p-2">
        <Outlet />
        <ForegroundAgentChat
          moduleKey="ap"
          currentPage={`/workflows/${workflowDefinitionId}`}
          onNavigate={(path, options) =>
            navigate({ to: path as any, search: options?.search as any })
          }
          onAddNode={addNode}
          onCreateWorkflowWithEmailRule={handleCreateWorkflowWithEmailRule}
          onSaveAndRunWorkflowOnInvoices={handleSaveAndRunWorkflowOnInvoices}
        />
      </div>
    );
  }

  if (!definition) return null;

  return (
    <div className="p-2">
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{definition.name}</h1>
            {definition.description && (
              <p className="text-muted-foreground mt-1">
                {definition.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDuplicateModalOpen(true)}
            >
              <CopyIcon className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Badge variant={definition.isActive ? 'default' : 'secondary'}>
              {definition.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline">v{definition.version}</Badge>
          </div>
        </div>
        {definition.tags && definition.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {definition.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <Tabs value={activeTab} className="w-full">
        <TabsList>
          <TabsTrigger value="overview" asChild>
            <Link
              to={paths.authenticated.workflows.definition}
              params={{ workflowDefinitionId }}
            >
              Overview
            </Link>
          </TabsTrigger>
          <TabsTrigger value="steps" asChild>
            <Link
              to={
                `/workflows/${workflowDefinitionId}/steps` as unknown as '/workflows/$workflowDefinitionId/steps'
              }
              params={{ workflowDefinitionId }}
            >
              Steps
            </Link>
          </TabsTrigger>
          <TabsTrigger value="runs" asChild>
            <Link
              to={
                `/workflows/${workflowDefinitionId}/runs` as unknown as '/workflows/$workflowDefinitionId/runs'
              }
              params={{ workflowDefinitionId }}
            >
              Runs
            </Link>
          </TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <Outlet />
        </div>
      </Tabs>

      <DuplicateWorkflowModal
        open={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onSuccess={(newWorkflowId) => {
          void navigate({
            to: paths.authenticated.workflows.definition,
            params: { workflowDefinitionId: newWorkflowId },
          });
        }}
        sourceWorkflow={
          definition ? { id: definition.id, name: definition.name } : null
        }
      />

      <ForegroundAgentChat
        moduleKey="ap"
        currentPage={`/workflows/${workflowDefinitionId}`}
        onNavigate={(path, options) =>
          navigate({ to: path as any, search: options?.search as any })
        }
        onAddNode={addNode}
        onCreateWorkflowWithEmailRule={handleCreateWorkflowWithEmailRule}
        onSaveAndRunWorkflowOnInvoices={handleSaveAndRunWorkflowOnInvoices}
      />
    </div>
  );
}

export function WorkflowDefinitionLayoutPage() {
  return (
    <WorkflowEditorProvider>
      <WorkflowDefinitionLayoutPageInner />
    </WorkflowEditorProvider>
  );
}
